<template>
  <div class="date-picker-calendar mx-auto" @mouseleave="onCalendarMouseLeave">
    <date-picker
      ref="calendarRef"
      v-model.string="vModelValue"
      class="w-max"
      v-bind="datePickerProps"
      @dayclick="handleDatePickerClicked"
      @drag="onDrag"
      @daymouseleave="onMouseLeave"
    >
      <template #header-prev-button>
        <svg-icon name="arrow-left" class="w-4 h-4" />
      </template>
      <template #header-next-button>
        <svg-icon name="arrow-right" class="w-4 h-4" />
      </template>
    </date-picker>
  </div>
</template>

<script setup lang="ts">
import { eachDayOfInterval, add, sub, isBefore } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { DatePicker } from 'v-calendar'
import type { CalendarDay } from 'v-calendar/dist/types/src/utils/page.d.ts'

import 'v-calendar/style.css'

import { getCrossDaysData } from '@/service/product'
import { getUtcDate } from '@/utils/time'
import { ASYNC_DATA_KEYS, getAsyncDataKey } from '@/constants/async-data-keys'

import type { UiProductSelectedData } from '@/types/product'
import type { FbranchArrivaltimeInfo } from '@/types/api'

enum RangePickerState {
  NONE = 0,
  START,
  END,
}

const NEXT_STEP_MAP = {
  [RangePickerState.NONE]: RangePickerState.START,
  [RangePickerState.START]: RangePickerState.END,
  [RangePickerState.END]: RangePickerState.START,
}

const { locale } = useI18n()
const { formatInTimeZone } = await useI18nDateFns()

const route = useRoute()
const productId = computed(() => route.params.pid as string)

const { regionId } = useRegion()

const selectedDate = defineModel<[string, string]>({ default: ['', ''] })

const props = withDefaults(
  defineProps<{
    arrivalTimes: FbranchArrivaltimeInfo
    isCrossDay?: boolean
    checkOutTime?: string
  }>(),
  {
    isCrossDay: false,
    checkOutTime: '00:00:00',
  }
)

const emit = defineEmits<{
  (event: 'cross-day-item-selecting', value: string): void
  (event: 'cross-day-item-selected', value: UiProductSelectedData[]): void
}>()

const { data: crossDaysData, refresh: fetchCrossDayData } = await useAsyncData(
  getAsyncDataKey(ASYNC_DATA_KEYS.CROSS_DAYS_DATA, [productId.value, regionId.value || 0]),
  async () => {
    if (!props.arrivalTimes?.selldates) return
    const tmpDateTime = props.arrivalTimes.selldates[lastSelectedDate.value]?.[0]?.selltimes?.[0]?.selltime
    const queryParams = { sale_region_id: regionId.value, arrival_time: tmpDateTime }
    const { data } = await getCrossDaysData(productId.value, queryParams)
    return data
  },
  {
    immediate: false,
  }
)

const calendarRef = ref<InstanceType<typeof DatePicker>>()
// cross day
const currentState = ref(RangePickerState.NONE)
const lastSelectedDate = ref('')
const tmpSelectedStartDate = ref('')
const lastSelectedFullDate = ref<[string, string]>(['', ''])
const lastSelectedCrossDayItem = ref<UiProductSelectedData[]>([])

const vModelValue = computed({
  get() {
    return props.isCrossDay
      ? {
          start: selectedDate.value[0],
          end: selectedDate.value[1],
        }
      : selectedDate.value[0]
  },
  set(value) {
    if (value === null) {
      return
    }

    if (typeof value === 'string') {
      selectedDate.value = [value, '']
    } else {
      selectedDate.value = [value.start, value.end]
    }
  },
})

const timezone = computed(() => props.arrivalTimes.timezone || 'Asia/Taipei')

const startDate = computed(() => getUtcDate(props.arrivalTimes.start_day, timezone.value))

const firstAvailableDate = computed(() => {
  const _firstDate = props.arrivalTimes.selldates ? Object.keys(props.arrivalTimes.selldates)?.[0] : props.arrivalTimes.start_day
  return getUtcDate(_firstDate, props.arrivalTimes.timezone)
})

const endDate = computed(() => {
  const baseEndDate = calculateBaseEndDate(props.arrivalTimes.end_day, timezone.value)
  return props.isCrossDay ? add(baseEndDate, { days: 1 }) : baseEndDate
})

const datePickerProps = computed(() => ({
  // v-calendar locale map: https://github.com/nathanreyes/v-calendar/blob/master/src/utils/defaults/locales.js#L3
  locale: locale.value,
  expanded: true,
  borderless: true,
  color: 'niceday',
  minDate: firstAvailableDate.value,
  maxDate: endDate.value,
  isRange: props.isCrossDay,
  disabledDates: calendarDisabledDates.value,
  timezone: timezone.value,
  masks: {
    modelValue: 'YYYY-MM-DD',
  },
  dragAttribute: {
    highlight: {
      start: {
        fillMode: 'solid' as const,
      },
      base: {
        fillMode: 'light' as const,
      },
      end: {
        fillMode: 'solid' as const,
      },
    },
  },
}))

const datesInBetween = computed(() =>
  eachDayOfInterval({
    start: toZonedTime(startDate.value, timezone.value),
    end: toZonedTime(endDate.value, timezone.value),
  }).map(date => formatInTimeZone(date, timezone.value, 'yyyy-MM-dd'))
)
const calendarDisabledDates = ref<string[]>([])
const updateCalendarDisabledDates = (isPadding = false) => {
  let selectableDates: string[] = []
  // const { isPadding, hasOneMoreNight } = options

  if (isPadding) return datesInBetween.value

  switch (currentState.value) {
    case RangePickerState.START:
    case RangePickerState.END: {
      selectableDates =
        crossDaysData.value?.day_prices
          ?.filter(item => item.date)
          .map(item => {
            const utcItem = getUtcDate(item.date as string, timezone.value)
            return formatInTimeZone(add(utcItem, { days: 1 }), timezone.value, 'yyyy-MM-dd')
          }) || []
      /**
       * 處理跨日商品但在同一天的情況
       *
       * 由於後端已經計算過選取的 datetime 是否在商品的 checkout time 之前，前端無需再次判斷
       * 且當選取的開始日期時間可能包含 checkout time 前的商品時，後端回應會包含兩個同一天
       * 所以此處的處理只將選取的日期往前扣一天
       */
      const firstSelectableDate = formatInTimeZone(sub(getUtcDate(selectableDates[0], timezone.value), { days: 1 }), timezone.value, 'yyyy-MM-dd')
      if (selectableDates[0] === selectableDates[1] || selectableDates.length === 1) {
        selectableDates[0] = firstSelectableDate
      } else {
        // start datetime 在 checkout time 之後，因 selectableDates 預設是 maxCrossDays + 1 天，所以要往前補第一天的可選日期
        selectableDates.unshift(firstSelectableDate)
      }
      break
    }
    case RangePickerState.NONE:
      selectableDates = props.arrivalTimes.selldates ? Object.keys(props.arrivalTimes.selldates) : []
      break
  }
  calendarDisabledDates.value = datesInBetween.value.filter(date => !selectableDates.includes(date))
}

const handleDatePickerClicked = (date: CalendarDay) => {
  if (date.isDisabled || !date.id) {
    return false
  }
  if (props.isCrossDay) {
    // fixed the start date, when the start date is determined. only the end date can be changed.
    if (currentState.value === RangePickerState.END) {
      window.setTimeout(() => {
        const hasOneMoreNight = checkStartTimeOverNight()
        const start = crossDaysData.value?.day_prices?.[0]?.date?.split(' ')[0] || ''
        /**
         * 如果第一天的 datetime 跟 checkout time 比較，沒有多一晚，則 end date 要加一天( 一定是後一天 )
         * click 的 date 跟 start date 是同一天又沒有多一晚的話，要避免造成 end date 是同一天
         * */
        const end =
          !hasOneMoreNight && date.id === start
            ? formatInTimeZone(add(getUtcDate(date.id, timezone.value), { days: 1 }), timezone.value, 'yyyy-MM-dd')
            : date.id
        vModelValue.value = {
          start,
          end,
        }
        lastSelectedDate.value = end
        finishRangeSelect()
      }, 50)
    }
    lastSelectedDate.value = date.id
    // for clicked start date
    if (currentState.value === RangePickerState.NONE) {
      nextStep()
    }
  }
}

const nextStep = () => {
  currentState.value = NEXT_STEP_MAP[currentState.value]
}

const getCrossDayItem = () => {
  const crossDayPrices = crossDaysData.value?.day_prices || []
  const selectedItemDateString = formatInTimeZone(sub(getUtcDate(lastSelectedDate.value, timezone.value), { days: 1 }), timezone.value, 'yyyy-MM-dd')
  const selectedIdx = crossDayPrices.findLastIndex(item => item.date?.includes(selectedItemDateString))
  const crossDayItem = crossDayPrices.slice(0, selectedIdx + 1) || []

  return crossDayItem.length
    ? [
        ...(crossDayItem.map(item => ({
          dateTime: item.date || '',
          price: item.discount || 0,
          originPrice: item.origin_price || 0,
        })) as UiProductSelectedData[]),
        {
          dateTime: `${lastSelectedDate.value} ${props.checkOutTime}`,
          price: 0,
          originPrice: 0,
        },
      ]
    : crossDayPrices[0]
      ? [
          {
            dateTime: crossDayPrices[0].date || '',
            price: crossDayPrices[0].discount || 0,
            originPrice: crossDayPrices[0].origin_price || 0,
          },
        ]
      : []
}

function calculateBaseEndDate(endDay: string | undefined, timezone: string | undefined) {
  if (endDay) {
    return getUtcDate(endDay, timezone)
  }
  const nowInTimezone = getUtcDate(new Date(), timezone)
  return add(nowInTimezone, { days: 30 })
}

watch(
  currentState,
  async (newState, _oldState) => {
    switch (newState) {
      case RangePickerState.NONE:
      case RangePickerState.START:
        if (lastSelectedDate.value) {
          tmpSelectedStartDate.value = lastSelectedDate.value
          emit('cross-day-item-selecting', lastSelectedDate.value)
          updateCalendarDisabledDates(false)
          await fetchCrossDayData()
          const hasOneMoreNight = checkStartTimeOverNight()
          lastSelectedDate.value = formatInTimeZone(
            add(getUtcDate(lastSelectedDate.value, timezone.value), { days: hasOneMoreNight ? 0 : 1 }),
            timezone.value,
            'yyyy-MM-dd'
          )
          nextStep()
        }
        updateCalendarDisabledDates()
        break
      case RangePickerState.END:
        if (tmpSelectedStartDate.value && lastSelectedDate.value) {
          /**
           * 因 v-calendar 的 bug 在 range 任一值為 disabled 時，UI 顯示跟資料會脫勾，造成資料正確但 UI 錯誤的奇怪狀況
           * 所以此處先清空 selectedDate.value，讓全部日期都可以選擇
           */
          calendarDisabledDates.value = []
          selectedDate.value = [tmpSelectedStartDate.value, lastSelectedDate.value]
          lastSelectedFullDate.value = [tmpSelectedStartDate.value, lastSelectedDate.value]
          finishRangeSelect()
          tmpSelectedStartDate.value = ''
          lastSelectedDate.value = ''
          // 等待 UI 更新後再更新 disabled dates
          nextTick(updateCalendarDisabledDates)
        }
        break
    }
  },
  {
    immediate: true,
  }
)

// 實測 v-calendar 的 `disabledDates` props 更新時，calendar 會自動換到 `disabledDates` 的第一個日期的月份，所以透過 watch 來移動到第一個可選日期的月份
watch([calendarRef, firstAvailableDate], ([newCalendarRef, newFirstAvailableDate]) => {
  const [year, month] = formatInTimeZone(newFirstAvailableDate, timezone.value, 'yyyy-MM').split('-')
  newCalendarRef?.move({ month: Number(month), year: Number(year) })
})

// for click "重選", reset 狀態用
watch(
  selectedDate,
  () => {
    if (selectedDate.value[0] === '' && selectedDate.value[1] === '') {
      currentState.value = RangePickerState.NONE
      lastSelectedDate.value = ''
      tmpSelectedStartDate.value = ''
      lastSelectedFullDate.value = ['', '']
      lastSelectedCrossDayItem.value = []
    }
  },
  { deep: true }
)

onMounted(() => {
  resumeCalendarState()
})

function resumeCalendarState() {
  if (selectedDate.value[0] !== '' && selectedDate.value[1] !== '') {
    currentState.value = RangePickerState.END
  }

  if (selectedDate.value[0] !== '') {
    nextTick(async () => {
      calendarRef.value?.moveToValue('start')
      if (props.isCrossDay) {
        lastSelectedDate.value = selectedDate.value[0]
        await fetchCrossDayData()
        updateCalendarDisabledDates()
      }
    })
  }
}

function checkStartTimeOverNight() {
  const firstSellTime = props.arrivalTimes.selldates?.[lastSelectedDate.value]?.[0]?.selltimes?.[0]?.selltime || ''
  if (!firstSellTime) {
    return false
  }
  const selectedTime = getUtcDate(firstSellTime, timezone.value)
  const checkoutDateTime = getUtcDate(`${lastSelectedDate.value} ${props.checkOutTime}`, timezone.value)
  return isBefore(selectedTime, checkoutDateTime)
}

function finishRangeSelect() {
  if (!lastSelectedDate.value) {
    return
  }
  lastSelectedCrossDayItem.value = getCrossDayItem()
  emit('cross-day-item-selected', lastSelectedCrossDayItem.value)
}

// 因解除不了 v-calender date range selecting 狀態，將 UI 的 range dates fallback 顯示用
let timeoutNo: number = 0
const isDragging = ref(false)

function onDrag() {
  isDragging.value = true
}

function onMouseLeave() {
  isDragging.value = false
}

/** 離開了整個 calendar container 後，fallback 回去 */
function onCalendarMouseLeave() {
  window.clearTimeout(timeoutNo)
  timeoutNo = window.setTimeout(() => {
    if (isDragging.value) {
      return
    }
    selectedDate.value = [selectedDate.value[0], selectedDate.value[1]]
  }, 50)
}
</script>

<style lang="scss" src="@/assets/styles/v-calendar-overrides.scss"></style>
